import { KeyRound, ShieldCheck, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import type { RoleSummary } from '../../api/areas/admin';
import { Badge } from '../../components/Badge';
import { Callout, StatCard, StatGrid, type Column } from '../../components/blocks';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { useApi } from '../../lib/useApi';
import { ListSection, RefreshButton, useAdminCrumbs, useCatalogue } from './ui';

/** `/admin/roles`: what each role is allowed to do, and who holds it. */
export function RolesPage() {
  const { t } = useTranslation('admin');
  useDocumentTitle(t('roles.title'));
  const crumbs = useAdminCrumbs(t('roles.title'));
  const navigate = useNavigate();
  const result = useApi((signal) => api.listRoles({ signal }), []);
  const rows = result.status === 'success' ? result.data.items : [];
  const { roleName } = useCatalogue();

  const columns: Column<RoleSummary>[] = [
    {
      key: 'role',
      header: t('roles.col.role'),
      cell: (r) => (
        <Link to={`/admin/roles/${encodeURIComponent(r.code)}`} className="inline-block max-w-full text-primary hover:underline" title={r.code}>
          <span className="font-medium">{roleName(r.code, r.description)}</span>
          <span className="block font-mono text-xs font-normal text-muted-foreground">{r.code}</span>
        </Link>
      ),
    },
    { key: 'permissions', header: t('roles.col.permissions'), numeric: true, cell: (r) => t('roles.permissionCount', { count: r.permission_count }) },
    {
      key: 'sensitive',
      header: t('roles.col.sensitive'),
      cell: (r) => (r.sensitive_permission_count > 0 ? <Badge tone="warn">{r.sensitive_permission_count}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'fourEyes',
      header: t('roles.col.fourEyes'),
      cell: (r) => (r.four_eyes_permission_count > 0 ? <Badge tone="serious">{r.four_eyes_permission_count}</Badge> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'members',
      header: t('roles.col.members'),
      cell: (r) => (
        <span>
          {t('roles.memberCount', { count: r.member_count })}
          {r.staff_member_count !== r.member_count ? <span className="block text-xs text-muted-foreground">{t('roles.staffOf', { count: r.staff_member_count })}</span> : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('roles.title')} crumbs={crumbs} subtitle={t('roles.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-4">
        <Callout tone="info" title={t('note.fourEyesTitle')}>
          {t('note.fourEyesBody')}
        </Callout>
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('roles.stat.roles')} value={rows.length} icon={ShieldCheck} />
            <StatCard label={t('roles.stat.sensitive')} value={rows.filter((r) => r.sensitive_permission_count > 0).length} hint={t('role.needsMfaHint')} icon={KeyRound} tone="warn" status={t('role.needsMfa')} />
            <StatCard label={t('roles.stat.fourEyes')} value={rows.filter((r) => r.four_eyes_permission_count > 0).length} hint={t('role.needsTwoHint')} tone="serious" status={t('role.needsTwo')} />
            <StatCard label={t('roles.stat.grants')} value={rows.reduce((n, r) => n + r.staff_member_count, 0)} icon={Users} to="/admin/staff" linkLabel={t('staff.title')} />
          </StatGrid>
        ) : null}
        <ListSection
          result={result}
          rows={rows}
          columns={columns}
          rowKey={(r) => r.code}
          onRowClick={(r) => navigate(`/admin/roles/${encodeURIComponent(r.code)}`)}
          description={result.status === 'success' ? t('roles.count', { count: rows.length }) : undefined}
          errorTitle={t('roles.loadError')}
          empty={t('roles.empty')}
        />
      </div>
    </>
  );
}
